import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Select, Spinner, Avatar } from "@/components/UI";
import api from "@/lib/api";
import {
  FolderKanban, Trophy, Rocket, CheckCircle2,
  AlertTriangle, ClipboardCheck, CalendarClock, HeartPulse, Layers,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const tooltipStyle = { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 };
const MONTHS = [
  { value: "", label: "Semua Bulan" },
  { value: "1", label: "Jan" },
  { value: "2", label: "Feb" },
  { value: "3", label: "Mar" },
  { value: "4", label: "Apr" },
  { value: "5", label: "Mei" },
  { value: "6", label: "Jun" },
  { value: "7", label: "Jul" },
  { value: "8", label: "Agu" },
  { value: "9", label: "Sep" },
  { value: "10", label: "Okt" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Des" },
];

function buildYearOptions() {
  const now = new Date().getFullYear();
  const list = [];
  for (let y = now - 2; y <= now + 2; y++) list.push({ value: String(y), label: String(y) });
  return list;
}

function MiniStat({ icon: Icon, color, label, value, sub }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-slate-400">{label}</p>
        <p className="font-heading text-2xl font-bold tabular-nums text-slate-100">{value}</p>
        {sub && <p className="truncate text-[11px] text-slate-500">{sub}</p>}
      </div>
    </Card>
  );
}

export default function CompanyLaporan() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [yearSprintPerf, setYearSprintPerf] = useState(null);
  const [sprintPerf, setSprintPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setSprintPerf(null);
    api.get("/dashboard/performance", { params: { type: "sprint", year } })
      .then((r) => setYearSprintPerf(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (!month) {
      setSprintPerf(null);
    } else {
      setLoading(true);
      api.get("/dashboard/performance", { params: { type: "sprint", year, month } })
        .then((r) => setSprintPerf(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
    api.get("/dashboard/leaderboard", { params: { year, month: month || undefined } })
      .then((r) => setLeaderboard(r.data?.leaderboard || []))
      .catch(() => setLeaderboard([]));
  }, [year, month]);

  // View per tahun = rollup sprint per bulan mulainya (sama basisnya dengan view per bulan)
  const yearlySeries = useMemo(() => {
    const buckets = MONTHS.slice(1).map((m, i) => ({ month: i + 1, label: m.label, output: 0, allocated: 0, performance: null }));
    (yearSprintPerf?.series || []).forEach((r) => {
      const b = buckets[(r.month || 0) - 1];
      if (!b) return;
      b.output += r.output || 0;
      b.allocated += r.allocated || 0;
    });
    buckets.forEach((b) => {
      b.performance = b.allocated > 0 ? Math.min(100, Math.round((b.output / b.allocated) * 1000) / 10) : null;
    });
    return buckets;
  }, [yearSprintPerf]);

  const series = useMemo(() => {
    if (month && sprintPerf?.series) return sprintPerf.series;
    return yearlySeries;
  }, [month, sprintPerf, yearlySeries]);

  // Overall performance percentage for the currently filtered period (year, or year+month per sprint).
  const filteredOutput = series.reduce((s, p) => s + (p.output || 0), 0);
  const filteredAllocated = series.reduce((s, p) => s + (p.allocated || 0), 0);
  const overallPerformance = filteredAllocated > 0 ? Math.min(100, Math.round((filteredOutput / filteredAllocated) * 100)) : null;

  const completedProjects = projects.filter((p) => p.status === "completed").length;
  const totalProjects = projects.length;
  const completedTasks = summary?.tasks?.done ?? 0;
  const totalTasks = summary?.tasks?.total ?? 0;
  const overdueTasks = summary?.tasks?.overdue ?? 0;
  const projectProgress = summary?.project_progress || [];

  const sprint = summary?.active_sprint?.sprint;
  const daysLeft = sprint?.end_date
    ? Math.max(0, Math.ceil((new Date(sprint.end_date) - new Date()) / 86400000))
    : null;

  const health = summary?.sprint_health;
  const onTrack = health ? health.overdue === 0 && health.on_hold === 0 : true;

  if (loading) return <Spinner />;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="company-laporan-page">
      {/* Header + sprint card */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Laporan Perusahaan</h1>
          <p className="text-sm text-slate-400">Pantau progres sprint, performa tim, dan capaian pekerjaan secara keseluruhan.</p>
        </div>
        {sprint && (
          <Card className="flex items-center gap-3 px-4 py-3" data-testid="active-sprint-card">
            <Rocket size={18} className="text-sky-400" />
            <div>
              <p className="text-sm font-semibold text-slate-100">Sprint {sprint.name}</p>
              <p className="text-xs text-slate-500">
                {new Date(sprint.start_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}
                {" – "}
                {new Date(sprint.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}
              </p>
            </div>
            {daysLeft != null && (
              <span className="ml-2 flex items-center gap-1.5 text-xs text-slate-400">
                <CalendarClock size={13} /> {daysLeft} days remaining
              </span>
            )}
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Kolom utama */}
        <div className="space-y-6 lg:col-span-2">
          {/* Chart */}
          <Card className="p-5" data-testid="company-chart">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-semibold">
                  {month ? `Detail Bulan ${MONTHS.find((m) => m.value === month)?.label} — per Sprint` : `Poin per Bulan Tahun ${year}`}
                </h2>
                <p className="mt-0.5 text-xs text-slate-400">
                  Performance keseluruhan periode ini:{" "}
                  <span className="font-semibold text-amber-400">{overallPerformance != null ? `${overallPerformance}%` : "—"}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={year} onChange={(e) => setYear(e.target.value)} data-testid="company-year-select">
                  {buildYearOptions().map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </Select>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="company-month-select">
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Select>
              </div>
            </div>

            {series.length ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey={month ? "sprint" : "label"} tick={{ fill: "#64748B", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => (name === "Performance (%)" ? [`${value ?? 0}%`, "Performance"] : [`${value ?? 0} pts`, name])} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="allocated" name="Allocated (pts)" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={24} />
                  <Bar dataKey="output" name="Output (pts)" fill="#22C55E" radius={[4, 4, 0, 0]} maxBarSize={24} />
                  <Bar dataKey="performance" name="Performance (%)" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={24} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-500">Belum ada data untuk tahun/bulan ini.</p>
            )}
          </Card>

          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
            <MiniStat icon={FolderKanban} color="#3B82F6" label="Total Projects" value={totalProjects} sub={`${completedProjects} completed`} />
            <MiniStat
              icon={ClipboardCheck} color="#14B8A6" label="Tasks"
              value={<>{completedTasks}<span className="text-base font-semibold text-slate-500"> / {totalTasks}</span></>}
              sub="tasks finished"
            />
            <MiniStat icon={AlertTriangle} color="#F43F5E" label="Overdue" value={overdueTasks} sub="tasks late" />
          </div>

          {/* Sprint Health + Project Progress */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card className="p-5" data-testid="sprint-health-card">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HeartPulse size={18} className="text-slate-400" />
                  <h2 className="font-heading text-lg font-semibold">Sprint Health</h2>
                </div>
                {health && (
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${onTrack ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                    {onTrack ? "● On Track" : "● Perlu Perhatian"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-4 ${onTrack ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                  {onTrack ? <CheckCircle2 size={28} /> : <AlertTriangle size={26} />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-200">{onTrack ? "On Track" : "Perlu Perhatian"}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {onTrack ? "Sprint berjalan sesuai rencana. Tetap pertahankan!" : "Ada task terlambat / tertahan di sprint ini."}
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-800 pt-4 text-center">
                <div>
                  <p className="font-mono text-xl font-bold tabular-nums text-rose-400">{health?.overdue ?? 0}</p>
                  <p className="text-[11px] text-slate-500">Overdue</p>
                </div>
                <div>
                  <p className="font-mono text-xl font-bold tabular-nums text-amber-400">{health?.on_hold ?? 0}</p>
                  <p className="text-[11px] text-slate-500">On Hold</p>
                </div>
                <div>
                  <p className="font-mono text-xl font-bold tabular-nums text-sky-400">{health?.due_soon ?? 0}</p>
                  <p className="text-[11px] text-slate-500">Due Soon</p>
                </div>
              </div>
            </Card>

            <Card className="p-5" data-testid="project-progress-card">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers size={18} className="text-slate-400" />
                  <h2 className="font-heading text-lg font-semibold">Project Progress</h2>
                </div>
                <button onClick={() => navigate("/projects")} className="text-xs text-sky-400 hover:underline">
                  Lihat Semua →
                </button>
              </div>
              {projectProgress.length === 0 ? (
                <p className="text-sm text-slate-500">Belum ada project aktif.</p>
              ) : (
                <div className="space-y-3.5">
                  {projectProgress.slice(0, 6).map((p) => (
                    <div key={p.id}>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="truncate text-slate-300">{p.name}</span>
                        <span className="font-mono tabular-nums text-slate-400">{p.progress}%</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${p.progress}%`, backgroundColor: p.color || "#38BDF8" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Top Performers — kolom kanan */}
        <Card className="self-start p-5" data-testid="company-leaderboard">
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy size={18} className="text-amber-400" />
              <h2 className="font-heading text-lg font-semibold">Top Performers</h2>
            </div>
            <button onClick={() => navigate("/performance/team")} className="text-xs text-sky-400 hover:underline">
              Lihat Semua →
            </button>
          </div>
          <p className="mb-3 text-xs text-slate-500">
            {month ? `Bulan ${MONTHS.find((m) => m.value === month)?.label} ${year}` : `Tahun ${year}`}
          </p>
          {leaderboard.length === 0 ? (
            <p className="text-sm text-slate-500">Belum ada data.</p>
          ) : (
            <div className="divide-y divide-slate-800/60">
              {leaderboard.map((e, i) => (
                <div
                  key={e.user_id}
                  onClick={() => navigate(`/performance/${e.user_id}`)}
                  className="flex cursor-pointer items-center gap-3 py-3 hover:bg-white/[0.02]"
                  data-testid={`leaderboard-row-${e.user_id}`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold ${
                      i === 0 ? "bg-amber-400/20 text-amber-400"
                      : i === 1 ? "bg-slate-300/15 text-slate-300"
                      : i === 2 ? "bg-orange-500/15 text-orange-400"
                      : "bg-white/5 text-slate-500"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <Avatar src={e.avatar_url} name={e.name} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-200">{e.name}</p>
                    <p className="truncate text-xs text-slate-500">{e.job_title || "Employee"}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-bold tabular-nums text-slate-100">{e.output} pts</p>
                    <p className={`font-mono text-[11px] tabular-nums ${e.performance == null ? "text-slate-600" : e.performance >= 80 ? "text-emerald-400" : e.performance >= 50 ? "text-slate-400" : "text-rose-400"}`}>
                      {e.performance == null ? "—" : `${e.performance}%`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
